import {
  createTodayEventsWhere,
  gameSportcastIdWhere,
  topLeaguesWhere,
  topMatchesWhere
} from 'utils/constants/sportsbook/data-fields';
import { FAVORITE_TEAM_SPORT } from 'utils/constants/sportsbook/favorite-team';
import { getLivePreMatchSportTypes } from 'utils/sportsbook/sport-data';
import { ENotRealSports } from 'interfaces/not-real-sports';

export type TPrematchCategoryTypes =
  | 'topLeague'
  | 'outright'
  | 'multiples'
  | 'todayEvents'
  | 'team'
  | 'boostedOdds'
  | 'betBuilder'
  | 'coupon'
  | 'topMatches'
  | 'olympicCompetition';

type TQueryType = {
  sportAlias: string;
  boostedGameIDs?: number[] | null | undefined;
};

/*eslint no-magic-numbers:*/
export const createGetSportDataWhereObj = (
  sportAlias: string,
  category?: TPrematchCategoryTypes,
  sportTypes?: string,
  multipleSelectionsCompIds?: number[],
  boostedGameIDs?: number[] | null
): Record<string, any> => {
  const queries: {
    [key: string]: ({ sportAlias }: TQueryType) => any;
  } = {
    [ENotRealSports.topLeague]: ({ sportAlias }: TQueryType) => {
      return {
        ...topLeaguesWhere,
        sport: {
          type: {
            '@in': getLivePreMatchSportTypes(sportTypes)
          },
          ...(sportAlias && { alias: sportAlias })
        }
      };
    },
    [ENotRealSports.olympicCompetition]: () => {
      return {
        game: {
          '@or': [
            {
              type: { '@in': [0, 2] }
            },
            {
              visible_in_prematch: 1,
              type: 1
            }
          ]
        },
        sport: {
          type: {
            '@in': getLivePreMatchSportTypes(sportTypes)
          }
        },
        competition: { info: { is_olympic: true } }
      };
    },
    [ENotRealSports.outright]: ({ sportAlias }: TQueryType) => {
      return {
        sport: {
          type: {
            '@in': getLivePreMatchSportTypes(sportTypes)
          },
          ...(sportAlias && { alias: sportAlias })
        },
        game: {
          '@or': [
            { type: { '@in': [0, 2] } },
            { visible_in_prematch: 1, type: 1 }
          ],
          show_type: 'OUTRIGHT'
        }
      };
    },
    [ENotRealSports.todayEvents]: ({ sportAlias }: TQueryType) => {
      return {
        ...createTodayEventsWhere(),
        sport: {
          ...(sportAlias && { alias: sportAlias }),
          type: {
            '@in': getLivePreMatchSportTypes(sportTypes)
          }
        }
      };
    },
    [ENotRealSports.topMatches]: ({ sportAlias }: TQueryType) => {
      return {
        ...topMatchesWhere,
        sport: {
          type: {
            '@in': getLivePreMatchSportTypes(sportTypes)
          },
          ...(sportAlias && { alias: sportAlias })
        }
      };
    },
    [ENotRealSports.betBuilder]: ({ sportAlias }: TQueryType) => {
      return {
        game: {
          '@or': [
            {
              type: { '@in': [0, 2] }
            },
            {
              visible_in_prematch: 1,
              type: 1
            }
          ],
          sportcast_id: gameSportcastIdWhere
        },
        sport: {
          ...(sportAlias && { alias: sportAlias }),
          type: {
            '@in': getLivePreMatchSportTypes(sportTypes)
          }
        }
      };
    },
    [ENotRealSports.boostedOdds]: ({
      sportAlias,
      boostedGameIDs
    }: TQueryType) => {
      return {
        game: {
          type: {
            '@in': [0, 2]
          },
          id: {
            '@in': boostedGameIDs
          }
        },
        ...(sportAlias && {
          sport: {
            alias: sportAlias
          }
        })
      };
    },
    [ENotRealSports.multiples]: () => {
      return {
        competition: {
          id: { '@in': multipleSelectionsCompIds }
        }
      };
    },
    [ENotRealSports.team]: ({ sportAlias }: TQueryType) => {
      return {
        game: {
          '@or': [
            {
              team1_id: +(sportAlias || -1)
            },
            {
              team2_id: +(sportAlias || -1)
            }
          ]
        },
        sport: {
          alias: FAVORITE_TEAM_SPORT
        }
      };
    },
    default: ({ sportAlias }: TQueryType) => {
      return {
        game: {
          '@or': [
            {
              type: { '@in': [0, 2] }
            },
            {
              visible_in_prematch: 1,
              type: 1
            }
          ]
        },
        sport: {
          ...(sportAlias && { alias: sportAlias }),
          type: {
            '@in': getLivePreMatchSportTypes(sportTypes)
          }
        }
      };
    }
  };

  if (!category || (category && !queries[category])) {
    return queries.default({ sportAlias, boostedGameIDs });
  }

  return queries[category]({ sportAlias, boostedGameIDs });
};

export const createGetGamesWhereObj = (
  sportAlias: string,
  selectedCompetitions: number[],
  favoriteTeamId: string,
  boostedGameIDs?: number[],
  isBetBuilder?: boolean,
  isTopMatches?: boolean
): Record<string, any> => {
  const markets = {
    market: {
      '@or': [
        {
          type: {
            '@in': [
              'P1P2',
              'P1XP2',
              'MatchResult',
              'MatchWinner',
              '1X12X2',
              'BothTeamsToScore',
              'DrawNoBet',
              'EvenOddTotal',
              'MatchOddEvenTotal',
              'MatchTotal',
              'TotalGamesOver/Under',
              'TotalofSets',
              'CorrectScore',
              'HalfTimeFullTime',
              'BoutBetting',
              'Handicap',
              'TotalGoalsOver/Under',
              'TotalRounds',
              'Over/Under',
              'Handicap(NoDraw)',
              'TotalPointsOver/Under',
              'TriesHandicap',
              'GamesHandicap',
              'GamesTotal',
              'MapsHandicap',
              'MapsTotal',
              'MatchTotal2',
              'MatchTotal2Asian',
              'MatchHandicap2',
              'MatchHandicap2Asian'
            ]
          }
        },
        {
          type: 'AsianHandicap',
          main_order: {
            '@in': [1, 2, 3, 4]
          }
        },
        {
          type: 'AsianHandicapAsian',
          main_order: {
            '@in': [1, 2, 3, 4]
          }
        },
        {
          type: 'MatchHandicap',
          main_order: {
            '@in': [1, 2, 3, 4]
          }
        },
        {
          type: 'OverUnder',
          main_order: {
            '@in': [1, 2, 3, 4]
          }
        },
        {
          type: 'OverUnderAsian',
          main_order: {
            '@in': [1, 2, 3, 4]
          }
        },
        {
          type: 'OverUnder',
          base: {
            '@in': [2.5]
          }
        },
        {
          type: 'OverUnderAsian',
          base: {
            '@in': [2.5]
          }
        },
        {
          display_key: 'OUTRIGHT'
        },
        {
          type: 'Winner'
        },
        {
          type: 'TrapWinner'
        },
        {
          display_key: {
            '@in': [
              'WINNER',
              'HANDICAP',
              'TOTALS',
              'DOUBLE CHANCE',
              'BOTHTEAMTOSCORE'
            ]
          }
        }
      ]
    }
  };

  if (favoriteTeamId) {
    return {
      ...markets,
      game: {
        '@or': [
          {
            team1_id: +favoriteTeamId
          },
          {
            team2_id: +favoriteTeamId
          }
        ]
      },
      sport: {
        alias: FAVORITE_TEAM_SPORT
      },
      competition: {
        id: {
          '@in': selectedCompetitions
        }
      }
    };
  }

  const where: {
    [key: string]: any;
  } = {
    sport: {
      alias: sportAlias
    },
    competition: {
      id: {
        '@in': selectedCompetitions
      }
    },
    ...markets,
    game: {}
  };

  if (boostedGameIDs?.length) {
    where.game = {
      type: {
        '@in': [0, 2]
      },
      id: {
        '@in': boostedGameIDs
      }
    };
  } else {
    where.game = {
      '@or': [
        {
          type: {
            '@in': [0, 2]
          }
        },
        {
          visible_in_prematch: 1,
          type: 1
        }
      ]
    };
  }

  if (isBetBuilder) {
    where.game.sportcast_id = gameSportcastIdWhere;
  }

  if (isTopMatches) {
    where.game.promoted = true;
  }

  return where;
};
